import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { observer } from 'mobx-react-lite';

import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';

import { api } from 'api';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { useStore } from 'models/Provider';
import type { IJob } from 'models/types';

import { ApplicantsByStage } from './applicants-by-stage';
import { EmployeeSources } from './employee-sources';
import { OverviewStats } from './overview-stats';
import { TrafficSources } from './traffic-sources';

type Props = {
  job: IJob;
};
export const Statistics = observer<Props>(function JobStatistics({ job }) {
  const [fromDate, setFromDate] = useState('all');
  const statsQuery = useQuery({
    queryKey: ['job-statistics', job.id, fromDate],
    queryFn: () => {
      let date = '';
      let fmt = 'yyyy-MM-dd';

      if (fromDate === 'today') {
        date = format(new Date(), fmt);
      } else if (fromDate === 'seven') {
        date = format(subDays(new Date(), 7), fmt);
      } else if (fromDate === 'thirty') {
        date = format(subDays(new Date(), 30), fmt);
      }
      return api.jobs.loadStatistics(job.id, date);
    },
    staleTime: 1000 * 60 * 60,
  });

  if (!statsQuery.isSuccess) {
    return <Loading />;
  }

  return (
    <PageContent>
      <Stack space={6}>
        <StatisticsTimeSelect onSelect={setFromDate} fromDate={fromDate} />
        <OverviewStats data={statsQuery.data} />
        <ApplicantsByStage stages={job.jobStages} statistics={statsQuery.data} />
        <TrafficSources statistics={statsQuery.data} />
        <EmployeeSources statistics={statsQuery.data} />
      </Stack>
    </PageContent>
  );
});

type TimeSelectProps = {
  onSelect(value: string): void;
  fromDate: string;
};

const StatisticsTimeSelect = observer<TimeSelectProps>(function StatisticsTimeSelect({
  onSelect,
  fromDate,
}) {
  const { t } = useStore();
  const [el, setEl] = useState<HTMLElement | null>(null);

  const isMobile = useIsMobile();

  const items = useMemo(() => {
    return [
      { value: 'today', label: t('job.statistics.today') },
      { value: 'seven', label: t('job.statistics.seven') },
      { value: 'thirty', label: t('job.statistics.thirty') },
      { value: 'all', label: t('job.statistics.all') },
    ];
  }, [t]);

  useEffect(() => {
    setEl(document.getElementById('job-statistics-actions'));
  }, [isMobile]);

  if (!el) return null;

  return createPortal(
    <div style={{ minWidth: 200 }}>
      <Select
        isClearable={false}
        isSearchable={false}
        onChange={onSelect}
        prefix={t('job.statistics.from')}
        options={items}
        defaultValue={fromDate}
      />
    </div>,
    el,
    String(isMobile),
  );
});
